import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import PixelIcon from '@/components/ui/PixelIcon';
import { ENEMY_DEFINITIONS } from '@/config/enemies';
import type { PixelIconName } from '@/components/ui/PixelIcon';
import type { Position } from '@/types/game';
import type { EnemyState, WorldEnemyType } from '@/types/enemy';

interface Enemy3DProps {
  enemy: EnemyState;
  cellSize: number;
  isFrozen?: boolean;
  playerPosition?: Position;
  entranceDelay?: number;
}

// ---------------------------------------------------------------------------
// Shape Config System
// ---------------------------------------------------------------------------
interface ShapeConfig {
  widthRatio: number;
  heightRatio: number;
  depthRatio: number;
  borderRadius: string;
  extraTransform?: string;
}

const ENEMY_SHAPE_CONFIG: Record<WorldEnemyType, ShapeConfig> = {
  bramble_beetle: {
    widthRatio: 1.15,
    heightRatio: 0.85,
    depthRatio: 0.9,
    borderRadius: '5%',
  },
  tunnel_drone: {
    widthRatio: 0.75,
    heightRatio: 1.25,
    depthRatio: 0.7,
    borderRadius: '5%',
  },
  skid_mite: {
    widthRatio: 0.8,
    heightRatio: 0.8,
    depthRatio: 0.8,
    borderRadius: '35%',
  },
  cinder_hound: {
    widthRatio: 1.1,
    heightRatio: 0.9,
    depthRatio: 1.05,
    borderRadius: '5%',
  },
  echo_mirror: {
    widthRatio: 1.0,
    heightRatio: 1.0,
    depthRatio: 0.5,
    borderRadius: '5%',
    extraTransform: 'rotateZ(45deg)',
  },
};

// ---------------------------------------------------------------------------
// Face color palettes
// ---------------------------------------------------------------------------
const ENEMY_FACE_COLORS: Record<
  WorldEnemyType,
  { front: string; back: string; top: string; bottom: string; left: string; right: string }
> = {
  bramble_beetle: {
    front: 'linear-gradient(135deg, #7cd97c 0%, #6abf69 50%, #4fa34f 100%)',
    back: 'linear-gradient(135deg, #4fa34f 0%, #3d8a3d 50%, #2b722b 100%)',
    top: 'linear-gradient(135deg, #8fee8f 0%, #7cd97c 50%, #6abf69 100%)',
    bottom: 'linear-gradient(135deg, #3d8a3d 0%, #2b722b 50%, #1a5a1a 100%)',
    left: 'linear-gradient(135deg, #6fd06f 0%, #5ab85a 50%, #4a9f4a 100%)',
    right: 'linear-gradient(135deg, #6fd06f 0%, #5ab85a 50%, #4a9f4a 100%)',
  },
  tunnel_drone: {
    front: 'linear-gradient(135deg, #b8a88e 0%, #a0937d 50%, #887d68 100%)',
    back: 'linear-gradient(135deg, #887d68 0%, #706854 50%, #585040 100%)',
    top: 'linear-gradient(135deg, #d0c0a6 0%, #b8a88e 50%, #a0937d 100%)',
    bottom: 'linear-gradient(135deg, #706854 0%, #585040 50%, #40382c 100%)',
    left: 'linear-gradient(135deg, #ada08a 0%, #958878 50%, #7d7062 100%)',
    right: 'linear-gradient(135deg, #ada08a 0%, #958878 50%, #7d7062 100%)',
  },
  skid_mite: {
    front: 'linear-gradient(135deg, #96d8f0 0%, #7ec8e3 50%, #5db5d5 100%)',
    back: 'linear-gradient(135deg, #5db5d5 0%, #449fc0 50%, #2b89ab 100%)',
    top: 'linear-gradient(135deg, #b0e8ff 0%, #96d8f0 50%, #7ec8e3 100%)',
    bottom: 'linear-gradient(135deg, #449fc0 0%, #2b89ab 50%, #187396 100%)',
    left: 'linear-gradient(135deg, #8ad0ea 0%, #70c0dd 50%, #58b0d0 100%)',
    right: 'linear-gradient(135deg, #8ad0ea 0%, #70c0dd 50%, #58b0d0 100%)',
  },
  cinder_hound: {
    front: 'linear-gradient(135deg, #f0724a 0%, #e05c3a 50%, #c84428 100%)',
    back: 'linear-gradient(135deg, #c84428 0%, #a83218 50%, #882210 100%)',
    top: 'linear-gradient(135deg, #ff8a60 0%, #f0724a 50%, #e05c3a 100%)',
    bottom: 'linear-gradient(135deg, #a83218 0%, #882210 50%, #681808 100%)',
    left: 'linear-gradient(135deg, #e86842 0%, #d85232 50%, #c04020 100%)',
    right: 'linear-gradient(135deg, #e86842 0%, #d85232 50%, #c04020 100%)',
  },
  echo_mirror: {
    front: 'linear-gradient(135deg, #d4a0ff 0%, #c084fc 50%, #a866e8 100%)',
    back: 'linear-gradient(135deg, #a866e8 0%, #904cd0 50%, #7834b8 100%)',
    top: 'linear-gradient(135deg, #e8bbff 0%, #d4a0ff 50%, #c084fc 100%)',
    bottom: 'linear-gradient(135deg, #904cd0 0%, #7834b8 50%, #601ca0 100%)',
    left: 'linear-gradient(135deg, #cc94f8 0%, #b87aec 50%, #a464dc 100%)',
    right: 'linear-gradient(135deg, #cc94f8 0%, #b87aec 50%, #a464dc 100%)',
  },
};

// ---------------------------------------------------------------------------
// Idle animation pool (3 per type, randomly cycled)
// ---------------------------------------------------------------------------
interface IdleAnimationConfig {
  animate: Record<string, number | number[]>;
  transition: Record<string, { duration: number; repeat: number; ease: string }>;
}

type EnemyIdleKey = string;

const ENEMY_IDLE_POOLS: Record<WorldEnemyType, Record<EnemyIdleKey, IdleAnimationConfig>> = {
  bramble_beetle: {
    sway: {
      animate: { rotateZ: [0, -4, 4, 0] },
      transition: { rotateZ: { duration: 3, repeat: Infinity, ease: 'easeInOut' } },
    },
    breathe: {
      animate: { y: [0, -2, 0] },
      transition: { y: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' } },
    },
    nod: {
      animate: { rotateX: [10, 18, 10] },
      transition: { rotateX: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    },
  },
  tunnel_drone: {
    float: {
      animate: { y: [0, -4, 0] },
      transition: { y: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } },
    },
    wobble: {
      animate: { rotateZ: [0, -3, 3, 0] },
      transition: { rotateZ: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    },
    scan: {
      animate: { rotateY: [0, 15, -15, 0] },
      transition: { rotateY: { duration: 3, repeat: Infinity, ease: 'easeInOut' } },
    },
  },
  skid_mite: {
    skitter: {
      animate: { x: [0, -1.5, 1.5, 0] },
      transition: { x: { duration: 0.4, repeat: Infinity, ease: 'linear' } },
    },
    bounce: {
      animate: { y: [0, -3, 0] },
      transition: { y: { duration: 0.5, repeat: Infinity, ease: 'easeOut' } },
    },
    twitch: {
      animate: { rotateZ: [0, -6, 0, 6, 0] },
      transition: { rotateZ: { duration: 0.6, repeat: Infinity, ease: 'easeInOut' } },
    },
  },
  cinder_hound: {
    nod: {
      animate: { rotateX: [10, 18, 10] },
      transition: { rotateX: { duration: 1.2, repeat: Infinity, ease: 'easeInOut' } },
    },
    prowl: {
      animate: { x: [0, -2, 2, 0] },
      transition: { x: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    },
    breathe: {
      animate: { y: [0, -1.5, 0] },
      transition: { y: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } },
    },
  },
  echo_mirror: {
    spin: {
      animate: { rotateY: [0, 360] },
      transition: { rotateY: { duration: 6, repeat: Infinity, ease: 'linear' } },
    },
    pulse: {
      animate: { y: [0, -2, 0] },
      transition: { y: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    },
    tilt: {
      animate: { rotateZ: [0, 8, -8, 0] },
      transition: { rotateZ: { duration: 3, repeat: Infinity, ease: 'easeInOut' } },
    },
  },
};

// Get the first idle key for a type (used as default)
function getDefaultIdleKey(type: WorldEnemyType): string {
  return Object.keys(ENEMY_IDLE_POOLS[type])[0];
}

// ---------------------------------------------------------------------------
// Per-type blink config
// ---------------------------------------------------------------------------
interface BlinkConfig {
  intervalMin: number;
  intervalMax: number;
  duration: number;
  style: 'both' | 'pulse' | 'alternate' | 'slow' | 'shimmer';
}

const ENEMY_BLINK_CONFIG: Record<WorldEnemyType, BlinkConfig> = {
  bramble_beetle: { intervalMin: 2000, intervalMax: 1000, duration: 120, style: 'both' },
  tunnel_drone:   { intervalMin: 1500, intervalMax: 1500, duration: 200, style: 'pulse' },
  skid_mite:      { intervalMin: 1500, intervalMax: 1000, duration: 100, style: 'alternate' },
  cinder_hound:   { intervalMin: 3000, intervalMax: 1000, duration: 180, style: 'slow' },
  echo_mirror:    { intervalMin: 0, intervalMax: 0, duration: 0, style: 'shimmer' },
};

// ---------------------------------------------------------------------------
// Expression type
// ---------------------------------------------------------------------------
type EnemyExpression = 'neutral' | 'alert' | 'stunned';

// ---------------------------------------------------------------------------
// Animated face renderer (per-type, expression-aware, with blinking)
// ---------------------------------------------------------------------------
function renderEnemyFace(
  type: WorldEnemyType,
  cubeWidth: number,
  cubeHeight: number,
  isBlinking: boolean,
  isBlinkingLeft: boolean,
  expression: EnemyExpression,
): React.ReactNode {
  const baseUnit = Math.min(cubeWidth, cubeHeight);
  const alertScale = expression === 'alert' ? 1.15 : 1;
  const isStunnedExpr = expression === 'stunned';

  switch (type) {
    case 'bramble_beetle': {
      const eyeScaleY = isStunnedExpr ? 0.3 : isBlinking ? 0.1 : alertScale;
      return (
        <>
          {/* Heavy brow ridge */}
          <div
            style={{
              width: '72%',
              height: baseUnit * 0.05,
              marginTop: '10%',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: '1px',
            }}
          />
          {/* Eyes */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '18%',
              width: '70%',
              marginTop: '2%',
            }}
          >
            <motion.div
              style={{
                width: '30%',
                height: baseUnit * 0.12,
                background: 'rgba(0,0,0,0.8)',
                borderRadius: '1px',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: eyeScaleY }}
              transition={{ duration: 0.08 }}
            >
              {!isBlinking && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '15%',
                  left: '20%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
            <motion.div
              style={{
                width: '30%',
                height: baseUnit * 0.12,
                background: 'rgba(0,0,0,0.8)',
                borderRadius: '1px',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: eyeScaleY }}
              transition={{ duration: 0.08 }}
            >
              {!isBlinking && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '15%',
                  left: '20%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
          </div>
          {/* Mouth */}
          <motion.div
            style={{
              width: '40%',
              height: baseUnit * 0.04,
              marginTop: baseUnit * 0.14 + 'px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '1px',
            }}
            animate={{
              scaleX: expression === 'alert' ? 1.2 : isStunnedExpr ? 0.6 : [1, 1.05, 1],
              scaleY: isStunnedExpr ? 0.5 : 1,
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      );
    }

    case 'tunnel_drone': {
      // Drone: single sensor eye that pulses opacity instead of scaleY
      const pulseOpacity = isBlinking ? 0.3 : 1;
      return (
        <>
          <motion.div
            style={{
              width: '40%',
              height: baseUnit * 0.3,
              marginTop: '15%',
              background: isStunnedExpr
                ? 'radial-gradient(ellipse, rgba(100,100,100,0.9) 30%, rgba(0,0,0,0.8) 70%)'
                : 'radial-gradient(ellipse, rgba(255,80,80,0.9) 30%, rgba(0,0,0,0.8) 70%)',
              borderRadius: '50%',
              boxShadow: isStunnedExpr
                ? '0 0 4px rgba(100,100,100,0.5)'
                : '0 0 4px rgba(255,80,80,0.5)',
              position: 'relative',
              overflow: 'hidden',
            }}
            animate={{
              opacity: pulseOpacity,
              scale: expression === 'alert' ? 1.1 : 1,
            }}
            transition={{ duration: 0.15 }}
          >
            {!isStunnedExpr && (
              <div style={{
                position: 'absolute',
                top: '18%',
                left: '22%',
                width: '30%',
                height: '30%',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '50%',
              }} />
            )}
          </motion.div>
          {/* Vent slits */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: baseUnit * 0.03 + 'px',
              marginTop: '10%',
              alignItems: 'center',
              width: '50%',
            }}
          >
            <div style={{ width: '80%', height: baseUnit * 0.025, background: 'rgba(0,0,0,0.35)', borderRadius: '1px' }} />
            <div style={{ width: '60%', height: baseUnit * 0.025, background: 'rgba(0,0,0,0.3)', borderRadius: '1px' }} />
            <div style={{ width: '40%', height: baseUnit * 0.025, background: 'rgba(0,0,0,0.25)', borderRadius: '1px' }} />
          </div>
        </>
      );
    }

    case 'skid_mite': {
      // Mite: alternate blink (left then right)
      const leftScaleY = isStunnedExpr ? 0.3 : isBlinkingLeft ? 0.1 : alertScale;
      const rightScaleY = isStunnedExpr ? 0.3 : (isBlinking && !isBlinkingLeft) ? 0.1 : alertScale;
      return (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '22%',
              width: '65%',
              marginTop: '14%',
              alignItems: 'flex-end',
            }}
          >
            <motion.div
              style={{
                width: baseUnit * 0.18,
                height: baseUnit * 0.18,
                background: 'rgba(0,0,0,0.75)',
                borderRadius: '50%',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: leftScaleY }}
              transition={{ duration: 0.06 }}
            >
              {!isBlinkingLeft && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '18%',
                  left: '22%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
            <motion.div
              style={{
                width: baseUnit * 0.13,
                height: baseUnit * 0.13,
                background: 'rgba(0,0,0,0.75)',
                borderRadius: '50%',
                marginBottom: baseUnit * 0.03 + 'px',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: rightScaleY }}
              transition={{ duration: 0.06 }}
            >
              {!(isBlinking && !isBlinkingLeft) && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '18%',
                  left: '22%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
          </div>
          {/* Mouth */}
          <motion.div
            style={{
              width: '25%',
              height: baseUnit * 0.14,
              marginTop: '8%',
              background: 'rgba(0,0,0,0.45)',
              borderRadius: '50%',
            }}
            animate={{
              scaleX: expression === 'alert' ? 1.15 : isStunnedExpr ? 0.5 : [1, 1.08, 1],
              scaleY: isStunnedExpr ? 0.5 : [1, 1.05, 1],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      );
    }

    case 'cinder_hound': {
      // Hound: slow deliberate blinks
      const houndScaleY = isStunnedExpr ? 0.15 : isBlinking ? 0.08 : alertScale;
      return (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '18%',
              width: '68%',
              marginTop: '14%',
            }}
          >
            <motion.div
              style={{
                width: '28%',
                height: baseUnit * 0.06,
                background: isStunnedExpr ? 'rgba(150,150,150,0.7)' : 'rgba(255,200,50,0.9)',
                borderRadius: '1px',
                transform: 'rotate(25deg)',
                boxShadow: isStunnedExpr ? 'none' : '0 0 3px rgba(255,200,50,0.5)',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: houndScaleY }}
              transition={{ duration: 0.12 }}
            >
              {!isBlinking && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '-20%',
                  left: '15%',
                  width: '35%',
                  height: '80%',
                  background: 'rgba(255,255,255,0.5)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
            <motion.div
              style={{
                width: '28%',
                height: baseUnit * 0.06,
                background: isStunnedExpr ? 'rgba(150,150,150,0.7)' : 'rgba(255,200,50,0.9)',
                borderRadius: '1px',
                transform: 'rotate(-25deg)',
                boxShadow: isStunnedExpr ? 'none' : '0 0 3px rgba(255,200,50,0.5)',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{ scaleY: houndScaleY }}
              transition={{ duration: 0.12 }}
            >
              {!isBlinking && !isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '-20%',
                  left: '15%',
                  width: '35%',
                  height: '80%',
                  background: 'rgba(255,255,255,0.5)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
          </div>
          {/* V-shaped snarl */}
          <motion.div
            style={{
              width: '42%',
              height: baseUnit * 0.1,
              marginTop: '10%',
              clipPath: 'polygon(0% 0%, 50% 100%, 100% 0%)',
              background: 'rgba(0,0,0,0.6)',
              position: 'relative',
            }}
            animate={{
              scaleX: expression === 'alert' ? 1.15 : isStunnedExpr ? 0.7 : 1,
            }}
            transition={{ duration: 0.2 }}
          />
          {/* Fang marks */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '30%',
              width: '42%',
              marginTop: '-' + (baseUnit * 0.02) + 'px',
            }}
          >
            <div
              style={{
                width: baseUnit * 0.03,
                height: baseUnit * 0.05,
                background: 'rgba(255,255,255,0.8)',
                borderRadius: '0 0 1px 1px',
              }}
            />
            <div
              style={{
                width: baseUnit * 0.03,
                height: baseUnit * 0.05,
                background: 'rgba(255,255,255,0.8)',
                borderRadius: '0 0 1px 1px',
              }}
            />
          </div>
        </>
      );
    }

    case 'echo_mirror': {
      // Mirror: no blink, eyes shimmer with rotation
      return (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '26%',
              width: '60%',
              marginTop: '20%',
            }}
          >
            <motion.div
              style={{
                width: baseUnit * 0.1,
                height: baseUnit * 0.1,
                background: isStunnedExpr ? 'rgba(150,150,150,0.5)' : 'rgba(255,255,255,0.7)',
                transform: 'rotate(45deg)',
                boxShadow: isStunnedExpr ? 'none' : '0 0 3px rgba(255,255,255,0.4)',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{
                rotate: isStunnedExpr ? 45 : [45, 405],
                scale: expression === 'alert' ? 1.15 : 1,
              }}
              transition={{
                rotate: { duration: 4, repeat: Infinity, ease: 'linear' },
                scale: { duration: 0.2 },
              }}
            >
              {!isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '10%',
                  left: '15%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
            <motion.div
              style={{
                width: baseUnit * 0.1,
                height: baseUnit * 0.1,
                background: isStunnedExpr ? 'rgba(150,150,150,0.5)' : 'rgba(255,255,255,0.7)',
                transform: 'rotate(45deg)',
                boxShadow: isStunnedExpr ? 'none' : '0 0 3px rgba(255,255,255,0.4)',
                position: 'relative',
                overflow: 'hidden',
              }}
              animate={{
                rotate: isStunnedExpr ? 45 : [45, 405],
                scale: expression === 'alert' ? 1.15 : 1,
              }}
              transition={{
                rotate: { duration: 4, repeat: Infinity, ease: 'linear', delay: 0.5 },
                scale: { duration: 0.2 },
              }}
            >
              {!isStunnedExpr && (
                <div style={{
                  position: 'absolute',
                  top: '10%',
                  left: '15%',
                  width: '35%',
                  height: '35%',
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                }} />
              )}
            </motion.div>
          </div>
          {/* Mouth */}
          <motion.div
            style={{
              width: '22%',
              height: baseUnit * 0.02,
              marginTop: '12%',
              background: 'rgba(255,255,255,0.4)',
              borderRadius: '1px',
            }}
            animate={{
              scaleX: isStunnedExpr ? 0.5 : [1, 1.1, 1],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      );
    }

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Decorative appendage renderer (unchanged from original)
// ---------------------------------------------------------------------------
function renderAppendages(
  type: WorldEnemyType,
  cubeWidth: number,
  cubeHeight: number,
): React.ReactNode {
  switch (type) {
    case 'bramble_beetle':
      return (
        <>
          <div style={{ position: 'absolute', bottom: '-8%', left: '10%', width: cubeWidth * 0.16, height: cubeWidth * 0.16, background: 'rgba(0,0,0,0.3)', clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)', transform: 'rotate(-10deg)' }} />
          <div style={{ position: 'absolute', bottom: '-8%', right: '10%', width: cubeWidth * 0.16, height: cubeWidth * 0.16, background: 'rgba(0,0,0,0.3)', clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)', transform: 'rotate(10deg)' }} />
          <div style={{ position: 'absolute', top: '-14%', left: '22%', width: cubeWidth * 0.03, height: cubeHeight * 0.2, background: 'rgba(0,0,0,0.35)', borderRadius: '2px', transform: 'rotate(-20deg)', transformOrigin: 'bottom center' }} />
          <div style={{ position: 'absolute', top: '-14%', right: '22%', width: cubeWidth * 0.03, height: cubeHeight * 0.2, background: 'rgba(0,0,0,0.35)', borderRadius: '2px', transform: 'rotate(20deg)', transformOrigin: 'bottom center' }} />
        </>
      );

    case 'tunnel_drone':
      return (
        <>
          <div style={{ position: 'absolute', top: '30%', left: '-18%', width: cubeWidth * 0.22, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.15)', borderRadius: '2px 0 0 2px', border: '1px solid rgba(0,0,0,0.2)' }} />
          <div style={{ position: 'absolute', top: '30%', right: '-18%', width: cubeWidth * 0.22, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.15)', borderRadius: '0 2px 2px 0', border: '1px solid rgba(0,0,0,0.2)' }} />
          <div style={{ position: 'absolute', top: '-6%', left: '20%', width: '60%', height: cubeHeight * 0.03, background: 'rgba(0,0,0,0.3)', borderRadius: '2px' }} />
          <div style={{ position: 'absolute', top: '-9%', left: '45%', width: cubeWidth * 0.1, height: cubeWidth * 0.1, background: 'rgba(0,0,0,0.3)', borderRadius: '50%' }} />
        </>
      );

    case 'skid_mite':
      return (
        <>
          <div style={{ position: 'absolute', bottom: '-6%', left: '15%', width: cubeWidth * 0.08, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.25)', borderRadius: '0 0 2px 2px' }} />
          <div style={{ position: 'absolute', bottom: '-6%', left: '32%', width: cubeWidth * 0.08, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.25)', borderRadius: '0 0 2px 2px' }} />
          <div style={{ position: 'absolute', bottom: '-6%', right: '32%', width: cubeWidth * 0.08, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.25)', borderRadius: '0 0 2px 2px' }} />
          <div style={{ position: 'absolute', bottom: '-6%', right: '15%', width: cubeWidth * 0.08, height: cubeHeight * 0.1, background: 'rgba(0,0,0,0.25)', borderRadius: '0 0 2px 2px' }} />
          <div style={{ position: 'absolute', top: '-8%', left: '25%', width: cubeWidth * 0.08, height: cubeWidth * 0.08, background: 'rgba(0,0,0,0.2)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', top: '-8%', right: '25%', width: cubeWidth * 0.08, height: cubeWidth * 0.08, background: 'rgba(0,0,0,0.2)', borderRadius: '50%' }} />
        </>
      );

    case 'cinder_hound':
      return (
        <>
          <div style={{ position: 'absolute', top: '-16%', left: '8%', width: cubeWidth * 0.18, height: cubeHeight * 0.2, background: 'rgba(0,0,0,0.25)', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', transform: 'rotate(-8deg)' }} />
          <div style={{ position: 'absolute', top: '-16%', right: '8%', width: cubeWidth * 0.18, height: cubeHeight * 0.2, background: 'rgba(0,0,0,0.25)', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', transform: 'rotate(8deg)' }} />
          <div style={{ position: 'absolute', top: '15%', right: '-16%', width: cubeWidth * 0.25, height: cubeHeight * 0.06, background: 'rgba(0,0,0,0.25)', borderRadius: '2px', transform: 'rotate(-30deg)', transformOrigin: 'left center' }} />
        </>
      );

    case 'echo_mirror':
      return (
        <>
          <div style={{ position: 'absolute', top: '10%', left: '10%', width: '115%', height: cubeHeight * 0.02, background: 'rgba(255,255,255,0.3)', transform: 'rotate(-45deg)', transformOrigin: 'left center', borderRadius: '1px' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 40%, rgba(255,255,255,0.1) 60%, transparent 100%)', borderRadius: '5%', pointerEvents: 'none' }} />
        </>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Frozen "Zzz" effect overlay
// ---------------------------------------------------------------------------
function FrozenZzzEffect({ cubeWidth }: { cubeWidth: number }) {
  const baseSize = Math.max(cubeWidth * 0.35, 8);

  return (
    <div
      style={{
        position: 'absolute',
        top: '-40%',
        right: '-10%',
        pointerEvents: 'none',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0,
      }}
    >
      <motion.span
        animate={{ y: [-2, -10], opacity: [1, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 0 }}
        style={{ fontSize: baseSize, fontWeight: 900, color: '#8ecfff', textShadow: '0 0 4px rgba(142, 207, 255, 0.6)', lineHeight: 1, fontFamily: 'monospace' }}
      >
        Z
      </motion.span>
      <motion.span
        animate={{ y: [-2, -8], opacity: [1, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 0.4 }}
        style={{ fontSize: baseSize * 0.7, fontWeight: 800, color: '#8ecfff', textShadow: '0 0 3px rgba(142, 207, 255, 0.5)', lineHeight: 1, fontFamily: 'monospace', marginRight: 2 }}
      >
        z
      </motion.span>
      <motion.span
        animate={{ y: [-1, -6], opacity: [0.8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 0.8 }}
        style={{ fontSize: baseSize * 0.5, fontWeight: 700, color: '#8ecfff', textShadow: '0 0 2px rgba(142, 207, 255, 0.4)', lineHeight: 1, fontFamily: 'monospace', marginRight: 4 }}
      >
        z
      </motion.span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const Enemy3D: React.FC<Enemy3DProps> = ({
  enemy,
  cellSize,
  isFrozen = false,
  playerPosition,
  entranceDelay = 0,
}) => {
  const def = ENEMY_DEFINITIONS[enemy.type];
  const faceColors = ENEMY_FACE_COLORS[enemy.type];
  const shapeConfig = ENEMY_SHAPE_CONFIG[enemy.type];
  const blinkConfig = ENEMY_BLINK_CONFIG[enemy.type];
  const prevDirectionRef = useRef(enemy.direction);

  const enemySize = cellSize * 0.72;
  const baseCubeSize = enemySize * 0.91;
  const cubeWidth = baseCubeSize * shapeConfig.widthRatio;
  const cubeHeight = baseCubeSize * shapeConfig.heightRatio;
  const cubeDepth = baseCubeSize * shapeConfig.depthRatio;
  const halfDepth = cubeDepth / 2;

  // ---------------------------------------------------------------------------
  // Blink + idle animation RAF loop (same pattern as Character3D)
  // ---------------------------------------------------------------------------
  const [isBlinking, setIsBlinking] = useState(false);
  const [isBlinkingLeft, setIsBlinkingLeft] = useState(false);
  const [currentIdleKey, setCurrentIdleKey] = useState(() => getDefaultIdleKey(enemy.type));

  useEffect(() => {
    // Mirror type doesn't blink - skip RAF for blink but still cycle idle
    const hasBlinking = blinkConfig.style !== 'shimmer';

    let rafId: number;
    let lastBlinkTime = performance.now();
    let nextBlinkDelay = hasBlinking
      ? blinkConfig.intervalMin + Math.random() * blinkConfig.intervalMax
      : Infinity;
    let blinkEndTime = 0;
    let alternatePhase = false; // For mite alternate blink

    let lastIdleTime = performance.now();
    let nextIdleDelay = 3000 + Math.random() * 2000;

    const idleKeys = Object.keys(ENEMY_IDLE_POOLS[enemy.type]);

    const tick = (now: number) => {
      // Blink logic
      if (hasBlinking) {
        if (blinkEndTime > 0) {
          if (now >= blinkEndTime) {
            // For alternate style, handle second eye
            if (blinkConfig.style === 'alternate' && !alternatePhase) {
              alternatePhase = true;
              setIsBlinkingLeft(false);
              setIsBlinking(true);
              blinkEndTime = now + blinkConfig.duration;
            } else {
              setIsBlinking(false);
              setIsBlinkingLeft(false);
              blinkEndTime = 0;
              alternatePhase = false;
              lastBlinkTime = now;
              nextBlinkDelay = blinkConfig.intervalMin + Math.random() * blinkConfig.intervalMax;
            }
          }
        } else if (now - lastBlinkTime >= nextBlinkDelay) {
          setIsBlinking(true);
          if (blinkConfig.style === 'alternate') {
            setIsBlinkingLeft(true);
            alternatePhase = false;
          }
          blinkEndTime = now + blinkConfig.duration;
        }
      }

      // Idle animation cycling
      if (now - lastIdleTime >= nextIdleDelay) {
        const randomKey = idleKeys[Math.floor(Math.random() * idleKeys.length)];
        setCurrentIdleKey(randomKey);
        lastIdleTime = now;
        nextIdleDelay = 3000 + Math.random() * 2000;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [enemy.type, blinkConfig]);

  // ---------------------------------------------------------------------------
  // Expression: neutral / alert / stunned
  // ---------------------------------------------------------------------------
  const expression: EnemyExpression = useMemo(() => {
    if (enemy.isStunned) return 'stunned';
    if (playerPosition) {
      const dx = Math.abs(enemy.position[0] - playerPosition[0]);
      const dy = Math.abs(enemy.position[1] - playerPosition[1]);
      if (dx + dy <= 3) return 'alert';
    }
    return 'neutral';
  }, [enemy.isStunned, enemy.position, playerPosition]);

  // ---------------------------------------------------------------------------
  // Movement detection
  // ---------------------------------------------------------------------------
  const isMoving = useMemo(() => {
    return (
      enemy.position[0] !== enemy.previousPosition[0] ||
      enemy.position[1] !== enemy.previousPosition[1]
    );
  }, [enemy.position, enemy.previousPosition]);

  const direction = useMemo(() => {
    if (isMoving) {
      prevDirectionRef.current = enemy.direction;
    }
    return isMoving ? enemy.direction : null;
  }, [isMoving, enemy.direction]);

  const enemyPosition = useMemo(() => {
    return {
      left: enemy.position[0] * cellSize + (cellSize - enemySize) / 2,
      top: enemy.position[1] * cellSize + (cellSize - enemySize) / 2,
    };
  }, [enemy.position, cellSize, enemySize]);

  const zIndex = enemy.position[1] * 100 + enemy.position[0] + 1000;

  // ---------------------------------------------------------------------------
  // Current idle animation config from pool
  // ---------------------------------------------------------------------------
  const currentIdleAnim = useMemo(() => {
    const pool = ENEMY_IDLE_POOLS[enemy.type];
    return pool[currentIdleKey] || pool[getDefaultIdleKey(enemy.type)];
  }, [enemy.type, currentIdleKey]);

  // ---------------------------------------------------------------------------
  // Cube animate/transition (uses current cycled idle animation)
  // ---------------------------------------------------------------------------
  const cubeAnimate = useMemo(() => {
    if (isMoving && direction && !isFrozen) {
      return {
        rotateX: direction === 'up' ? -20 : direction === 'down' ? 25 : 10,
        rotateY: direction === 'left' ? 20 : direction === 'right' ? -20 : 0,
        rotateZ: 0 as number,
        scaleX: direction === 'left' || direction === 'right' ? 1.15 : 0.9,
        scaleY: direction === 'up' || direction === 'down' ? 1.15 : 0.9,
        x: 0 as number,
        y: 0 as number,
      };
    }

    if (isFrozen) {
      return {
        rotateX: 10 as number | number[],
        rotateY: 0 as number | number[],
        rotateZ: [-2, 2, -2] as number | number[],
        scaleX: 1,
        scaleY: 1,
        x: 0 as number | number[],
        y: 0 as number | number[],
      };
    }

    if (!enemy.isStunned) {
      return {
        rotateX: 10 as number | number[],
        rotateY: 0 as number | number[],
        rotateZ: 0 as number | number[],
        scaleX: 1,
        scaleY: 1,
        x: 0 as number | number[],
        y: 0 as number | number[],
        ...currentIdleAnim.animate,
      };
    }

    return {
      rotateX: 10,
      rotateY: 0,
      rotateZ: 0,
      scaleX: 1,
      scaleY: 1,
      x: 0,
      y: 0,
    };
  }, [isMoving, direction, enemy.isStunned, isFrozen, currentIdleAnim]);

  const cubeTransition = useMemo(() => {
    const base = {
      scaleX: { type: 'spring' as const, stiffness: 300, damping: 15, mass: 0.8 },
      scaleY: { type: 'spring' as const, stiffness: 300, damping: 15, mass: 0.8 },
      rotateX: { type: 'spring' as const, stiffness: 400, damping: 30 },
      rotateY: { type: 'spring' as const, stiffness: 400, damping: 30 },
    };

    if (isFrozen) {
      return {
        ...base,
        rotateZ: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' as const },
      };
    }

    if (!isMoving && !enemy.isStunned) {
      return { ...base, ...currentIdleAnim.transition };
    }

    return base;
  }, [isMoving, enemy.isStunned, isFrozen, currentIdleAnim]);

  // Shared cube face style
  const faceBase: React.CSSProperties = {
    position: 'absolute',
    border: '3px solid rgba(0, 0, 0, 0.3)',
    boxShadow:
      'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
    backfaceVisibility: 'hidden',
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        width: enemySize,
        height: enemySize,
        zIndex,
        pointerEvents: 'none',
      }}
      initial={{
        left: enemyPosition.left,
        top: enemyPosition.top,
        scale: 0,
        opacity: 0,
      }}
      animate={{
        left: enemyPosition.left,
        top: enemyPosition.top,
        opacity: enemy.isStunned ? 0.4 : 1,
        scale: enemy.isStunned ? 0.85 : 1,
        filter: isFrozen ? 'brightness(0.85) saturate(0.4) hue-rotate(180deg)' : 'none',
      }}
      transition={{
        left: { type: 'spring', stiffness: 300, damping: 25 },
        top: { type: 'spring', stiffness: 300, damping: 25 },
        opacity: { duration: 0.3 },
        scale: { type: 'spring', stiffness: 200, damping: 20, delay: entranceDelay },
        filter: { duration: 0.4 },
      }}
    >
      {/* Perspective wrapper */}
      <motion.div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          perspective: '500px',
        }}
        animate={
          enemy.isStunned
            ? { x: [-2, 2, -2, 2, 0] }
            : { x: 0 }
        }
        transition={
          enemy.isStunned
            ? { x: { duration: 0.3, repeat: Infinity, ease: 'linear' } }
            : { x: { duration: 0 } }
        }
      >
        {/* 3D Cube */}
        <motion.div
          style={{
            width: cubeWidth,
            height: cubeHeight,
            position: 'relative',
            transformStyle: 'preserve-3d',
            transformOrigin: 'center center',
          }}
          animate={cubeAnimate}
          transition={cubeTransition}
        >
          {/* Shape wrapper - applies extraTransform (e.g. rotateZ(45deg) for echo_mirror diamond) to ALL faces as a unit */}
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              transformStyle: 'preserve-3d',
              transform: shapeConfig.extraTransform || 'none',
            }}
          >
            {/* Front face */}
            <div
              style={{
                ...faceBase,
                width: '103%',
                height: '103%',
                left: '-1.5%',
                top: '-1.5%',
                transform: `translateZ(${halfDepth}px)`,
                background: faceColors.front,
                borderRadius: shapeConfig.borderRadius,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden',
              }}
            >
              {renderEnemyFace(enemy.type, cubeWidth, cubeHeight, isBlinking, isBlinkingLeft, expression)}
            </div>

            {/* Back face */}
            <div
              style={{
                ...faceBase,
                width: '103%',
                height: '103%',
                left: '-1.5%',
                top: '-1.5%',
                transform: `translateZ(-${halfDepth}px) rotateY(180deg)`,
                background: faceColors.back,
                borderRadius: shapeConfig.borderRadius,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  opacity: 0.3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <PixelIcon
                  name={def.icon as PixelIconName}
                  size={Math.min(cubeWidth, cubeHeight) * 0.4}
                  color="rgba(0,0,0,0.5)"
                />
              </div>
            </div>

            {/* Top face - centered then translated to cube edge */}
            <div
              style={{
                ...faceBase,
                width: '103%',
                height: cubeDepth + 2,
                left: '-1.5%',
                top: (cubeHeight - (cubeDepth + 2)) / 2,
                transform: `translateY(-${cubeHeight / 2}px) rotateX(90deg)`,
                background: faceColors.top,
                borderRadius: shapeConfig.borderRadius,
              }}
            />

            {/* Bottom face - centered then translated to cube edge */}
            <div
              style={{
                ...faceBase,
                width: '103%',
                height: cubeDepth + 2,
                left: '-1.5%',
                top: (cubeHeight - (cubeDepth + 2)) / 2,
                transform: `translateY(${cubeHeight / 2}px) rotateX(-90deg)`,
                background: faceColors.bottom,
                borderRadius: shapeConfig.borderRadius,
              }}
            />

            {/* Left face - centered then translated to cube edge */}
            <div
              style={{
                ...faceBase,
                width: cubeDepth + 2,
                height: '103%',
                left: (cubeWidth - (cubeDepth + 2)) / 2,
                top: '-1.5%',
                transform: `translateX(-${cubeWidth / 2}px) rotateY(-90deg)`,
                background: faceColors.left,
                borderRadius: shapeConfig.borderRadius,
              }}
            />

            {/* Right face - centered then translated to cube edge */}
            <div
              style={{
                ...faceBase,
                width: cubeDepth + 2,
                height: '103%',
                left: (cubeWidth - (cubeDepth + 2)) / 2,
                top: '-1.5%',
                transform: `translateX(${cubeWidth / 2}px) rotateY(90deg)`,
                background: faceColors.right,
                borderRadius: shapeConfig.borderRadius,
              }}
            />
          </div>

          {/* Decorative appendages - pushed to front face Z level, also inside shape transform */}
          <div style={{
            position: 'absolute',
            inset: 0,
            transformStyle: 'preserve-3d',
            transform: shapeConfig.extraTransform || 'none',
          }}>
            <div style={{ position: 'absolute', inset: 0, transform: `translateZ(${halfDepth + 1}px)` }}>
              {renderAppendages(enemy.type, cubeWidth, cubeHeight)}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Frozen Zzz effect */}
      {isFrozen && <FrozenZzzEffect cubeWidth={cubeWidth} />}

      {/* Shadow */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '10%',
          width: '80%',
          height: '30%',
          background: 'radial-gradient(ellipse, rgba(0, 0, 0, 0.25) 0%, transparent 70%)',
          transform: 'scaleY(0.5)',
          pointerEvents: 'none',
        }}
      />
    </motion.div>
  );
};

export default Enemy3D;
